import { useState } from "react";
import { User, Users, Plus, UserMinus, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useInstitutionMemberships } from "../hooks";
import { InlineLoader } from "@/components/ui/pulsing-loader";
import { AddMemberDialog } from "./AddMemberDialog";
import { RemoveMemberDialog } from "./RemoveMemberDialog";
import { EditMemberDialog } from "./EditMemberDialog";
import { softDeleteInstitutionMember } from "@/Services/institutions.service";
import { useAuth } from "@/context/authContext";
import { usePermissions } from "@/hooks/usePermissions";
import { useQueryClient } from "@tanstack/react-query";
import type { MembershipWithPerson, InstitutionMember } from "../types";

interface Props {
  institutionId: string;
}

function MemberRow({
  membership,
  onRemove,
  onEdit,
  onDelete,
}: {
  membership: MembershipWithPerson;
  onRemove: (m: MembershipWithPerson) => void;
  onEdit: (m: InstitutionMember) => void;
  onDelete: (m: InstitutionMember) => void;
}) {
  const { can } = usePermissions();
  const canEdit = can("institution_member.edit");
  const canDelete = can("institution_member.remove");
  const isMinister = !!membership.minister;
  const person = membership.minister ?? membership.institution_member;
  const name = person?.full_name ?? "—";
  const subtitle = membership.minister?.adb_code ?? "Miembro";
  const institutionMember = membership.institution_member as InstitutionMember | null;

  return (
    <div className="flex items-center gap-3 rounded-lg border border-border bg-card p-3">
      <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-muted">
        <User className="size-5 text-muted-foreground" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{name}</p>
        <p className="truncate text-xs text-muted-foreground">{subtitle}</p>
      </div>
      <Badge variant={isMinister ? "default" : "secondary"} className="shrink-0">
        {isMinister ? "Ministro" : "Miembro"}
      </Badge>
      {!membership.is_active && (
        <Badge variant="outline" className="shrink-0 text-muted-foreground">
          Inactivo
        </Badge>
      )}
      {(canEdit || canDelete) && (
        <div className="ml-1 flex shrink-0 items-center gap-1">
          {/* Edit — only for institution_members, not for ministers */}
          {canEdit && !isMinister && institutionMember && (
            <button
              onClick={() => onEdit(institutionMember as InstitutionMember)}
              title="Editar miembro"
              className="rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <Pencil className="size-4" />
            </button>
          )}
          {/* Deactivate (soft remove from institution) */}
          {canEdit && membership.is_active && (
            <button
              onClick={() => onRemove(membership)}
              title="Dar de baja"
              className="rounded p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
            >
              <UserMinus className="size-4" />
            </button>
          )}
          {/* Hard delete — only for institution_members */}
          {canDelete && !isMinister && institutionMember && (
            <button
              onClick={() => onDelete(institutionMember as InstitutionMember)}
              title="Eliminar miembro"
              className="rounded p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
            >
              <Trash2 className="size-4" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function InstitutionMembersTab({ institutionId }: Props) {
  const { can } = usePermissions();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { data: memberships = [], isLoading } = useInstitutionMemberships(institutionId);

  const [addOpen, setAddOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<MembershipWithPerson | null>(null);
  const [editTarget, setEditTarget] = useState<InstitutionMember | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <InlineLoader message="Cargando miembros..." />
      </div>
    );
  }

  const active = memberships.filter((m) => m.is_active);
  const inactive = memberships.filter((m) => !m.is_active);

  async function handleDelete(member: InstitutionMember) {
    if (!user) return;
    const confirmed = window.confirm(
      `¿Eliminar permanentemente a ${member.full_name ?? "este miembro"}? Esta acción no se puede deshacer.`,
    );
    if (!confirmed) return;
    setDeleting(member.id);
    try {
      await softDeleteInstitutionMember(member.id, user.id);
      await queryClient.invalidateQueries({ queryKey: ["institution-memberships", institutionId] });
    } finally {
      setDeleting(null);
    }
  }

  return (
    <>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Users className="size-4" />
            <span>
              {active.length} miembro{active.length !== 1 ? "s" : ""} activo
              {active.length !== 1 ? "s" : ""}
            </span>
          </div>
          {can("institution_member.create") && (
            <Button size="sm" onClick={() => setAddOpen(true)} className="gap-1.5">
              <Plus className="size-4" />
              Agregar miembro
            </Button>
          )}
        </div>

        {memberships.length === 0 ? (
          <p className="py-12 text-center text-sm text-muted-foreground">
            No hay miembros registrados en esta institución.
          </p>
        ) : (
          <div className="space-y-2">
            {active.map((m) => (
              <MemberRow
                key={m.id}
                membership={m}
                onRemove={setRemoveTarget}
                onEdit={setEditTarget}
                onDelete={handleDelete}
              />
            ))}
            {inactive.length > 0 && (
              <>
                <p className="pt-4 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Inactivos
                </p>
                {inactive.map((m) => (
                  <MemberRow
                    key={m.id}
                    membership={m}
                    onRemove={setRemoveTarget}
                    onEdit={setEditTarget}
                    onDelete={handleDelete}
                  />
                ))}
              </>
            )}
          </div>
        )}
        {deleting && (
          <p className="text-center text-xs text-muted-foreground">Eliminando…</p>
        )}
      </div>

      <AddMemberDialog isOpen={addOpen} onClose={() => setAddOpen(false)} institutionId={institutionId} />

      {removeTarget && (
        <RemoveMemberDialog
          isOpen
          onClose={() => setRemoveTarget(null)}
          membership={removeTarget}
          institutionId={institutionId}
        />
      )}

      {editTarget && (
        <EditMemberDialog
          isOpen
          onClose={() => setEditTarget(null)}
          member={editTarget}
        />
      )}
    </>
  );
}
